import React, { useEffect, useMemo, useState } from 'react';
import { Box, Breadcrumbs, Button, IconButton, Link, ListItemIcon, ListItemText, Menu, MenuItem, TextField, Typography } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { FINANCE } from '../../../common/constants/routes.constants';
import { LocationDto } from '../../../common/interfaces/kindergarten.interface';
import { loadKindergartenHierarchy } from '../financeDirectory';
import { useTranslation } from 'react-i18next';

interface FinanceBreadcrumbsProps {
  kindergartenId: string;
  locationId: string;
  groupId?: string;
  /** Used while the hierarchy is still loading, so the crumb is not briefly blank. */
  fallbackNames?: { location?: string | null; group?: string | null };
}

interface Option {
  id: string;
  label: string;
}

/** Above this many options the menu gets a filter; below it a filter is just in the way. */
const FILTER_THRESHOLD = 8;

/**
 * One segment: a label that goes back up to that level, and - when there is anything to
 * switch to - a chevron that opens its siblings.
 *
 * The two are separate targets on purpose. Folding navigation into the menu would mean
 * that going *back* to a location takes two clicks, and going back is the thing an admin
 * does most. On the page you are already on there is nowhere to go back to, so the whole
 * segment opens the menu instead.
 */
const Segment: React.FC<{
  label: string;
  to?: string;
  options?: Option[];
  selectedId?: string;
  onSelect?: (id: string) => void;
  /** A level you have not picked yet: muted, and always opens the menu. */
  placeholder?: boolean;
  switchLabel: string;
}> = ({ label, to, options = [], selectedId, onSelect, placeholder, switchLabel }) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [filter, setFilter] = useState('');

  // with one option there is nothing to switch to, and a chevron would only invite a click
  // that does nothing - the same reasoning as showing a single kindergarten as plain text
  const canSwitch = !!onSelect && options.some(option => option.id !== selectedId);
  const shown = useMemo(
    () => options.filter(option => option.label.toLowerCase().includes(filter.trim().toLowerCase())),
    [options, filter]
  );

  const open = (event: React.MouseEvent<HTMLElement>) => {
    setFilter('');
    setAnchor(event.currentTarget);
  };

  const labelNode =
    to && !placeholder ? (
      // a real link, so it can be opened in a new tab and is announced as one
      <Link component={RouterLink} to={to} underline="hover" color="inherit">
        {label}
      </Link>
    ) : canSwitch ? (
      <Button
        onClick={open}
        endIcon={<ExpandMoreIcon />}
        aria-label={switchLabel}
        sx={{
          textTransform: 'none',
          p: 0,
          minWidth: 0,
          font: 'inherit',
          color: placeholder ? 'text.secondary' : 'text.primary',
          fontStyle: placeholder ? 'italic' : undefined,
          '& .MuiButton-endIcon': { ml: 0.25 }
        }}
      >
        {label}
      </Button>
    ) : (
      <Typography color={placeholder ? 'text.secondary' : 'text.primary'}>{label}</Typography>
    );

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      {labelNode}
      {to && !placeholder && canSwitch && (
        <IconButton size="small" onClick={open} aria-label={switchLabel} sx={{ ml: 0.25, p: 0.25 }}>
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      )}
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {options.length > FILTER_THRESHOLD && (
          <Box sx={{ px: 1.5, pb: 1 }} onKeyDown={event => event.stopPropagation()}>
            <TextField
              size="small"
              autoFocus
              fullWidth
              placeholder={t('breadcrumbs.filter')}
              value={filter}
              onChange={event => setFilter(event.target.value)}
            />
          </Box>
        )}
        {shown.map(option => (
          <MenuItem
            key={option.id}
            selected={option.id === selectedId}
            onClick={() => {
              setAnchor(null);
              if (option.id !== selectedId) onSelect?.(option.id);
            }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>{option.id === selectedId && <CheckIcon fontSize="small" />}</ListItemIcon>
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
        {shown.length === 0 && (
          <MenuItem disabled>
            <ListItemText>{t('breadcrumbs.nothingMatches')}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

/**
 * Where you are while drilling down through the overview: Overview › location › group.
 *
 * Only the levels under the section. The kindergarten is picked on the overview itself, and
 * the sidebar already says this is Finance, so neither repeats here. The page you are on can
 * be switched for a sibling - another location, another group - but only there: a chevron on
 * every level offered three ways of doing the same thing.
 *
 * The month travels with every link, so stepping sideways from September's group A lands on
 * September's group B rather than resetting to the current month.
 */
const FinanceBreadcrumbs: React.FC<FinanceBreadcrumbsProps> = ({ kindergartenId, locationId, groupId, fallbackNames }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [hierarchy, setHierarchy] = useState<LocationDto[]>([]);

  useEffect(() => {
    if (!kindergartenId) {
      setHierarchy([]);
      return undefined;
    }
    let cancelled = false;
    loadKindergartenHierarchy(kindergartenId)
      .then(items => !cancelled && setHierarchy(items))
      .catch(() => !cancelled && setHierarchy([]));
    return () => {
      cancelled = true;
    };
  }, [kindergartenId]);

  const month = new URLSearchParams(location.search).get('month');
  const query = () => {
    const params = new URLSearchParams({ kindergartenId });
    if (month) params.set('month', month);
    return params.toString();
  };
  const dashboardHref = `${FINANCE}?${query()}`;
  const locationHref = (id: string) => `${FINANCE}/location/${id}?${query()}`;
  const groupHref = (inLocation: string, id: string) => `${FINANCE}/location/${inLocation}/group/${id}?${query()}`;

  const currentLocation = hierarchy.find(item => item.id === locationId);
  const groupsHere = (currentLocation?.groups ?? []).filter((group): group is typeof group & { id: string } => !!group.id);
  const currentGroup = groupsHere.find(group => group.id === groupId);

  return (
    <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 1 }} aria-label={t('breadcrumbs.ariaLabel')}>
      <Segment label={t('nav.financeItems.overview')} to={dashboardHref} switchLabel={t('nav.financeItems.overview')} />

      <Segment
        label={currentLocation?.name ?? fallbackNames?.location ?? t('common.location')}
        to={groupId ? locationHref(locationId) : undefined}
        // the sibling picker only on the level you are on
        options={groupId ? [] : hierarchy.map(item => ({ id: item.id, label: item.name }))}
        selectedId={locationId}
        onSelect={groupId ? undefined : id => navigate(locationHref(id))}
        switchLabel={t('breadcrumbs.switchLocation')}
      />

      {groupId && (
        <Segment
          label={currentGroup?.name ?? fallbackNames?.group ?? t('common.group')}
          options={groupsHere.map(group => ({ id: group.id, label: group.name }))}
          selectedId={groupId}
          onSelect={id => navigate(groupHref(locationId, id))}
          switchLabel={t('breadcrumbs.switchGroup')}
        />
      )}
    </Breadcrumbs>
  );
};

export default FinanceBreadcrumbs;
